import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Snackbar, Alert as MuiAlert, Box } from "@mui/material";
import { useDarkMode, useDevMode } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import CounterCards from "../../components/Allocate/CounterCards";
import MasterDetailView from "../../components/Allocate/MasterDetailView";
import RequestCardGrid from "../../components/Allocate/RequestCardGrid";
import RequestsAccordion from "../../components/Allocate/RequestsAccordion";
import Alert from "../../components/ui/Alert";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import SearchBar from "../../components/ui/SearchBar";
import ActionButton from "../../components/ui/ActionButton";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";
import { useAllocateListingsData } from "../../hooks/useAllocateListingsData";
import { useRequestsData } from "../../hooks/useRequestsData";
import { useDevModeDonateMaterialsSetup } from "../../hooks/useDevModeDonateMaterialsSetup";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import config from "../../config";
import { isDateInPast } from "../../utils/dateUtils";
import {
  faSync,
  faGrip,
  faColumns,
  faTableCells,
} from "@fortawesome/free-solid-svg-icons";
import "../../style/Card.css";
import "../../style/StatusFilterTabs.css";
import "../../style/Allocate.css";

function Allocate() {
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();
  const { registerDevAction, clearDevActions } = useDevMode();

  // permission guard - redirects to /no-permission if user lacks access
  const { hasPermission, isChecking } = usePermissionGuard("donor", "allocate");

  // Timer refs for cleanup
  const refreshTimerRef = useRef(null);
  const justUpdatedTimerRef = useRef(null);

  const [showAllListings, setShowAllListings] = useState(
    config.isDemoMode ? config.devShowAllListings : false
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("master"); // "master", "card", "table"
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [materialFilter, setMaterialFilter] = useState("all");
  const [receiverFilter, setReceiverFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedListingForMaster, setSelectedListingForMaster] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleToggleShowAll = useCallback((newValue) => {
    console.log(`toggling showAllListings to: ${newValue}`);
    setShowAllListings(newValue);
  }, []);

  useDevModeDonateMaterialsSetup(
    registerDevAction,
    clearDevActions,
    handleToggleShowAll
  );

  // data management hook - using the new listings-with-requests approach
  const {
    loading,
    counters,
    listingsData,
    errorMessage,
    lastUpdated,
    performRefresh,
    handleAllocate: allocateMaterial,
    handleDecline: declineRequest,
  } = useAllocateListingsData(showAllListings);

  // also use old requests data for card view
  const {
    requestsData,
    mostRecentRequest,
    performRefresh: refreshRequests,
    handleAllocate: allocateRequestOld,
    handleDecline: declineRequestOld,
  } = useRequestsData(showAllListings);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Trigger fade-in animation after loading completes
  useEffect(() => {
    if (!initialLoading && !loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, loading]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (justUpdatedTimerRef.current) clearTimeout(justUpdatedTimerRef.current);
    };
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // wrapper for allocate with snackbar (master-detail view)
  const handleAllocate = async (requestId, quantity) => {
    const result = await allocateMaterial(requestId, quantity);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? "success" : "error",
    });
  };

  // wrapper for decline with snackbar (master-detail view)
  const handleDecline = async (requestId) => {
    const result = await declineRequest(requestId);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? "success" : "error",
    });
  };

  // wrappers for card view
  const handleAllocateCard = async (requestId, quantity) => {
    const result = await allocateRequestOld(requestId, quantity);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? "success" : "error",
    });
  };

  const handleDeclineCard = async (requestId) => {
    const result = await declineRequestOld(requestId);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? "success" : "error",
    });
  };

  // handle request change (for card view)
  const handleRequestChange = () => {
    refreshRequests();
  };

  // refresh handler
  const handleRefreshClick = async () => {
    setRefreshing(true);
    setJustUpdated(false);

    try {
      if (viewMode === "master") {
        await performRefresh();
      } else {
        await refreshRequests();
      }
    } catch (error) {
      console.error("refresh error:", error);
    }

    refreshTimerRef.current = setTimeout(() => {
      setRefreshing(false);
      // Trigger slide-up animation on counter values
      setJustUpdated(true);
      // Clear animation class after it completes
      justUpdatedTimerRef.current = setTimeout(() => {
        setJustUpdated(false);
      }, 500);
    }, 2000);
  };

  // classify request status for filtering
  const getRequestStatus = (request) => {
    if (!request.active) return "inactive";
    if (request.date_delivered) return "delivered";
    if (request.pickup_confirmed === 1) return "awaiting_pickup";
    if (request.date_allocated) return "approved";
    if (request.scheduled_date && isDateInPast(request.scheduled_date)) return "expired";
    return "pending";
  };

  // filter listings based on search term (for master-detail view)
  const filteredListingsData = useMemo(() => (listingsData || []).filter((listing) => {
    if (!searchTerm.trim()) return true;

    const search = searchTerm.toLowerCase();

    // search in listing fields
    if (listing.material?.toLowerCase().includes(search)) return true;
    if (listing.id?.toLowerCase().includes(search)) return true;

    // search in nested requests
    if (
      listing.requests?.some((req) => {
        return (
          req.receiver_company?.toLowerCase().includes(search) ||
          req.client_id?.toLowerCase().includes(search) ||
          req.receiver_city?.toLowerCase().includes(search)
        );
      })
    )
      return true;

    return false;
  }), [listingsData, searchTerm]);

  // extract unique materials and receivers for dropdown options
  const uniqueMaterials = useMemo(
    () => [...new Set((requestsData || []).map((r) => r.material).filter(Boolean))].sort(),
    [requestsData]
  );
  const uniqueReceivers = useMemo(
    () => [...new Set((requestsData || []).map((r) => r.receiver_company).filter(Boolean))].sort(),
    [requestsData]
  );

  // filter requests for card view
  const filteredRequestsData = useMemo(() => (requestsData || [])
    .filter((request) => {
      // status filter (empty set = show all)
      if (statusFilter.size > 0) {
        if (!statusFilter.has(getRequestStatus(request))) return false;
      }

      // material filter
      if (materialFilter !== "all") {
        if (request.material !== materialFilter) return false;
      }

      // receiver filter
      if (receiverFilter !== "all") {
        if (request.receiver_company !== receiverFilter) return false;
      }

      // search filter
      if (!searchTerm.trim()) return true;

      const search = searchTerm.toLowerCase();

      return (
        request.id?.toString().includes(search) ||
        request.listing_id?.toString().includes(search) ||
        request.client_id?.toLowerCase().includes(search) ||
        request.material?.toLowerCase().includes(search) ||
        request.receiver_company?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.date_modified || b.date_created || 0) - new Date(a.date_modified || a.date_created || 0);
        case "oldest":
          return new Date(a.date_modified || a.date_created || 0) - new Date(b.date_modified || b.date_created || 0);
        case "pickup_asc": {
          const dateA = a.scheduled_date ? new Date(a.scheduled_date) : new Date("9999-12-31");
          const dateB = b.scheduled_date ? new Date(b.scheduled_date) : new Date("9999-12-31");
          return dateA - dateB;
        }
        case "pickup_desc": {
          const dateA2 = a.scheduled_date ? new Date(a.scheduled_date) : new Date(0);
          const dateB2 = b.scheduled_date ? new Date(b.scheduled_date) : new Date(0);
          return dateB2 - dateA2;
        }
        case "qty_desc":
          return (b.quantity || b.max_qty || 0) - (a.quantity || a.max_qty || 0);
        case "qty_asc":
          return (a.quantity || a.min_qty || 0) - (b.quantity || b.min_qty || 0);
        default:
          return 0;
      }
    }),
    [requestsData, statusFilter, materialFilter, receiverFilter, searchTerm, sortBy]
  );

  // compute status counts for filter tabs (respects search + material + receiver, but not status)
  const statusCounts = useMemo(() => (requestsData || []).reduce(
    (acc, request) => {
      // apply search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          request.id?.toString().includes(search) ||
          request.listing_id?.toString().includes(search) ||
          request.client_id?.toLowerCase().includes(search) ||
          request.material?.toLowerCase().includes(search) ||
          request.receiver_company?.toLowerCase().includes(search);
        if (!matchesSearch) return acc;
      }
      // apply material filter
      if (materialFilter !== "all" && request.material !== materialFilter) return acc;
      // apply receiver filter
      if (receiverFilter !== "all" && request.receiver_company !== receiverFilter) return acc;

      const status = getRequestStatus(request);
      acc.all++;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { all: 0, pending: 0, approved: 0, awaiting_pickup: 0, delivered: 0, expired: 0, inactive: 0 }
  ), [requestsData, searchTerm, materialFilter, receiverFilter]);

  // transform counters for CounterCards component
  // card click: switch to master-detail view with listing pre-selected
  const handleCardClick = useCallback((request) => {
    setSelectedListingForMaster(request.listing_id);
    setSearchTerm("");
    setViewMode("master");
  }, []);

  const displayCounters = {
    requests: counters.totalRequests,
    pending: counters.pendingRequests,
    allocated: counters.totalRequests - counters.pendingRequests,
  };

  // show loading while checking permissions
  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        text={t("Checking permissions...")}
        darkMode={darkMode}
      />
    );
  }

  // if no permission, the hook will redirect - but just in case, don't render
  if (!hasPermission) {
    return null;
  }

  if (initialLoading || loading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Fetching your listings...")}
      />
    );
  }

  return (
    <>
      <div className={`allocate-container ${isVisible ? "visible" : ""}`}>
        {/* Page header with last updated badge */}
        <div className="page-header-row">
          <div /> {/* Spacer */}
          <LastUpdatedBadge 
            timestamp={lastUpdated}
            onRefresh={handleRefreshClick}
            isRefreshing={refreshing}
          />
        </div>

        {/* error message banner */}
        {errorMessage && (
          <div className="allocate-content-wrapper">
            <Alert message={errorMessage} severity="error" />
          </div>
        )}

        {/* counter cards section */}
        <div className={`counter-cards-wrapper ${refreshing ? "shimmer-overlay" : ""}`}>
          <CounterCards
            counters={displayCounters}
            mostRecentRequest={mostRecentRequest}
            isLoading={refreshing}
            justUpdated={justUpdated}
          />
        </div>

        {/* page header with search and view toggle */}
        <div className="allocate-controls-wrapper">
          <Box className="allocate-header">
            <h2 className="allocate-title">
              {viewMode === "master" ? t("Your Listings") : t("All Requests")}
              <span className="listings-count">
                (
                {viewMode === "master"
                  ? filteredListingsData.length
                  : filteredRequestsData.length}
                )
              </span>
            </h2>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {/* view toggle - 3 options */}
              <Box className="view-toggle-buttons">
                <ActionButton
                  variant={viewMode === "master" ? "success" : "default"}
                  icon={faColumns}
                  iconOnly
                  tooltip={t("Master-Detail View")}
                  onClick={() => setViewMode("master")}
                />
                <ActionButton
                  variant={viewMode === "card" ? "success" : "default"}
                  icon={faGrip}
                  iconOnly
                  tooltip={t("Card View")}
                  onClick={() => {
                    setViewMode("card");
                    setSelectedListingForMaster(null);
                  }}
                />
                <ActionButton
                  variant={viewMode === "table" ? "success" : "default"}
                  icon={faTableCells}
                  iconOnly
                  tooltip={t("Table View")}
                  onClick={() => {
                    setViewMode("table");
                    setSelectedListingForMaster(null);
                  }}
                />
              </Box>

              {/* refresh button */}
              <ActionButton
                variant="default"
                icon={faSync}
                iconOnly
                tooltip={t("Refresh")}
                onClick={handleRefreshClick}
                disabled={refreshing}
                spinning={refreshing}
              />

              {/* search bar */}
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t("Search listings or receivers...")}
                darkMode={darkMode}
              />
            </Box>
          </Box>
        </div>

        {/* Content area based on view mode */}
        <div className={`allocate-content-wrapper ${refreshing ? "shimmer-overlay" : ""}`}>
          {/* Master-Detail View (default) */}
          {viewMode === "master" &&
            (filteredListingsData.length === 0 ? (
              <Box className="no-requests">
                <p>
                  {searchTerm
                    ? t("No listings match your search")
                    : t(
                        "No listings found. Create listings in Donate Materials."
                      )}
                </p>
              </Box>
            ) : (
              <MasterDetailView
                listings={filteredListingsData}
                darkMode={darkMode}
                onAllocate={handleAllocate}
                onDecline={handleDecline}
                initialSelectedId={selectedListingForMaster}
              />
            ))}

          {/* Card View */}
          {viewMode === "card" && (
            <>
              {/* Filter Bar */}
              <div className={`allocate-filter-bar ${darkMode ? "dark" : "light"}`}>
                {/* Status chips row */}
                <div className={`status-filter-tabs ${darkMode ? "dark" : "light"}`}>
                  {[
                    { key: "all", label: t("All") },
                    { key: "pending", label: t("Pending") },
                    { key: "awaiting_pickup", label: t("Awaiting Pickup") },
                    { key: "approved", label: t("Approved") },
                    { key: "delivered", label: t("Delivered") },
                    { key: "expired", label: t("Expired") },
                  ]
                    .filter((tab) => tab.key === "all" || statusCounts[tab.key] > 0)
                    .map((tab) => {
                      const isAllTab = tab.key === "all";
                      const isActive = isAllTab
                        ? statusFilter.size === 0
                        : statusFilter.has(tab.key);
                      return (
                        <button
                          key={tab.key}
                          className={`status-filter-tab ${isActive ? "active" : ""}`}
                          onClick={() => {
                            if (isAllTab) {
                              setStatusFilter(new Set());
                            } else {
                              setStatusFilter((prev) => {
                                const next = new Set(prev);
                                if (next.has(tab.key)) {
                                  next.delete(tab.key);
                                } else {
                                  next.add(tab.key);
                                }
                                return next;
                              });
                            }
                          }}
                        >
                          {tab.label}
                          <span className="status-filter-count">{statusCounts[tab.key] || 0}</span>
                        </button>
                      );
                    })}
                </div>

                {/* Dropdowns row */}
                <div className="filter-dropdowns-row">
                  {/* Material filter */}
                  <div className="filter-dropdown-group">
                    <label className="filter-dropdown-label">{t("Material")}</label>
                    <select
                      className={`filter-dropdown ${darkMode ? "dark" : "light"}`}
                      value={materialFilter}
                      onChange={(e) => setMaterialFilter(e.target.value)}
                    >
                      <option value="all">{t("All Materials")}</option>
                      {uniqueMaterials.map((mat) => (
                        <option key={mat} value={mat}>{mat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Receiver filter */}
                  <div className="filter-dropdown-group">
                    <label className="filter-dropdown-label">{t("Receiver")}</label>
                    <select
                      className={`filter-dropdown ${darkMode ? "dark" : "light"}`}
                      value={receiverFilter}
                      onChange={(e) => setReceiverFilter(e.target.value)}
                    >
                      <option value="all">{t("All Receivers")}</option>
                      {uniqueReceivers.map((rec) => (
                        <option key={rec} value={rec}>{rec}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort dropdown */}
                  <div className="filter-dropdown-group">
                    <label className="filter-dropdown-label">{t("Sort By")}</label>
                    <select
                      className={`filter-dropdown ${darkMode ? "dark" : "light"}`}
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="newest">{t("Newest First")}</option>
                      <option value="oldest">{t("Oldest First")}</option>
                      <option value="pickup_asc">{t("Pickup ↑ Soonest")}</option>
                      <option value="pickup_desc">{t("Pickup ↓ Latest")}</option>
                      <option value="qty_desc">{t("Quantity ↓ High")}</option>
                      <option value="qty_asc">{t("Quantity ↑ Low")}</option>
                    </select>
                  </div>

                  {/* Clear filters button (show only when filters are active) */}
                  {(statusFilter.size > 0 || materialFilter !== "all" || receiverFilter !== "all" || sortBy !== "newest") && (
                    <button
                      className={`filter-clear-btn ${darkMode ? "dark" : "light"}`}
                      onClick={() => {
                        setStatusFilter(new Set());
                        setMaterialFilter("all");
                        setReceiverFilter("all");
                        setSortBy("newest");
                      }}
                    >
                      {t("Clear Filters")}
                    </button>
                  )}
                </div>
              </div>

              {filteredRequestsData.length === 0 ? (
              <Box className="no-requests">
                <p>
                  {searchTerm || statusFilter.size > 0 || materialFilter !== "all" || receiverFilter !== "all"
                    ? t("No requests match your filters")
                    : t("No requests yet")}
                </p>
              </Box>
            ) : (
              <RequestCardGrid
                requests={filteredRequestsData}
                darkMode={darkMode}
                onCardClick={handleCardClick}
              />
            )}
            </>
          )}

          {/* Table View */}
          {viewMode === "table" &&
            (filteredRequestsData.length === 0 ? (
              <Box className="no-requests">
                <p>
                  {searchTerm
                    ? t("No requests match your search")
                    : t("No requests yet")}
                </p>
              </Box>
            ) : (
              <RequestsAccordion
                expanded="requests-accordion"
                onAccordionChange={() => {}}
                requestsTableRef={{ current: null }}
                requestsData={filteredRequestsData}
                darkMode={darkMode}
                handleRefresh={handleRefreshClick}
                isRefreshing={refreshing}
                onAllocate={handleAllocateCard}
                onRequestChange={handleRequestChange}
                onDecline={handleDeclineCard}
              />
            ))}
        </div>

        {/* success/error notification snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MuiAlert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {t(snackbar.message)}
          </MuiAlert>
        </Snackbar>
      </div>
    </>
  );
}

export default Allocate;
